/**
 * Migrate to Supabase Utility
 * 
 * One-time migration script to move existing localStorage data to Supabase.
 * 
 * This utility:
 * 1. Reads current localStorage data
 * 2. Transforms data (adds exit_status, altitude, dedupeStatus)
 * 3. Extracts ValueProposal records from existing attribute values
 * 4. Inserts into Supabase tables
 * 5. Marks migration complete
 * 6. Clears old localStorage entries (optional)
 */

import { getSupabaseClient } from '../lib/supabaseClient';
import { isSupabaseAvailable, upsertMany, insertMany, toDbFormat } from '../lib/supabaseSync';
import { migrateItem } from '../lib/stateMigration';

// UUID v4 regex pattern
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID v4
 */
function isValidUUID(id: string): boolean {
  return UUID_REGEX.test(id);
}

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Ensure an ID is a valid UUID, generate one if not
 * Tracks old->new ID mappings for reference updates
 */
function ensureUUID(id: string, idMap: Map<string, string>): string {
  if (isValidUUID(id)) {
    return id;
  }
  
  // Check if we already generated a UUID for this ID
  if (idMap.has(id)) {
    return idMap.get(id)!;
  }
  
  // Generate a new UUID and track the mapping
  const newId = generateUUID();
  idMap.set(id, newId);
  return newId;
}

// ============================================================================
// Types
// ============================================================================

interface MigrationResult {
  success: boolean;
  migratedCounts: {
    candidates: number;
    cycles: number;
    sessions: number;
    focusLists: number;
    clusters: number;
    attributes: number;
    orgUnits: number;
    valueProposals: number;
  };
  transformations: {
    detailedStateAdded: number;
    altitudeAdded: number;
    dedupeStatusAdded: number;
    valueProposalsExtracted: number;
  };
  errors: string[];
}

interface ValueProposal {
  id: string;
  item_id: string;
  attribute_key: string;
  value: unknown;
  status: 'proposed' | 'accepted' | 'rejected' | 'superseded';
  proposed_by: string;
  proposed_at: Date;
  accepted_by?: string;
  accepted_at?: Date;
  source: string;
}

interface MigrationOptions {
  clearLocalStorage?: boolean;  // Whether to clear localStorage after migration
  dryRun?: boolean;             // If true, only log what would be migrated
}

// ============================================================================
// LocalStorage Keys
// ============================================================================

const STORAGE_KEYS = {
  candidates: 'peak-priorities-candidates',
  cycles: 'peak-priorities-degap-cycles',
  sessions: 'peak-priorities-sessions',
  focusLists: 'peak-priorities-focus-lists',
  clusters: 'peak-priorities-clusters',
  attributes: 'attributes-storage',
  enterpriseIdentity: 'enterprise-identity-storage',
  migrationComplete: 'peak-priorities-migration-complete',
};

const TABLE_NAMES = {
  candidates: 'candidates',
  cycles: 'degap_cycles',
  sessions: 'prioritization_sessions',
  focusLists: 'focus_lists',
  clusters: 'item_clusters',
  clusterMemberships: 'cluster_memberships',
  attributes: 'attributes',
  orgUnits: 'org_units',
  valueProposals: 'value_proposals',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Read persisted Zustand data from localStorage
 */
function readFromLocalStorage<T>(key: string): T | null {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    // Zustand persist middleware stores data in a 'state' property
    return parsed.state || parsed;
  } catch (error) {
    console.error(`[Migration] Failed to read ${key} from localStorage:`, error);
    return null;
  }
}

/**
 * Convert Date strings back to Date objects
 */
function convertDates<T extends Record<string, unknown>>(obj: T, dateFields: string[]): T {
  const result = { ...obj };
  for (const field of dateFields) {
    if (result[field] && typeof result[field] === 'string') {
      (result as Record<string, unknown>)[field] = new Date(result[field] as string);
    }
  }
  return result;
}

// ============================================================================
// Migration Functions
// ============================================================================

/**
 * Check if migration has already been completed
 */
export function isMigrationComplete(): boolean {
  return localStorage.getItem(STORAGE_KEYS.migrationComplete) === 'true';
}

/**
 * Mark migration as complete
 */
function markMigrationComplete(): void {
  localStorage.setItem(STORAGE_KEYS.migrationComplete, 'true');
  localStorage.setItem('peak-priorities-migration-date', new Date().toISOString());
}

/**
 * Determine altitude based on item characteristics
 */
function inferAltitude(candidate: Record<string, unknown>): string {
  const title = (candidate.title as string || '').toLowerCase();
  const description = (candidate.description as string || '').toLowerCase();
  const tags = candidate.tags as string[] || [];
  
  // Check for theme-level indicators
  if (tags.includes('theme') || tags.includes('strategic') || title.includes('theme')) {
    return 'theme';
  }
  
  // Check for initiative-level indicators
  if (tags.includes('initiative') || tags.includes('epic') || title.includes('initiative')) {
    return 'initiative';
  }
  
  // Check for task-level indicators
  if (tags.includes('task') || tags.includes('bug') || title.startsWith('[task]')) {
    return 'task';
  }
  
  // Check for subtask-level indicators
  if (tags.includes('subtask') || title.startsWith('[subtask]')) {
    return 'subtask';
  }
  
  // Default to feature
  return 'feature';
}

/**
 * Extract value proposals from candidate attributes
 */
function extractValueProposals(
  candidate: Record<string, unknown>
): ValueProposal[] {
  const proposals: ValueProposal[] = [];
  const attributes = candidate.attributes as Array<{ attributeKey: string; value: unknown }> | undefined;
  
  if (!attributes || attributes.length === 0) {
    return proposals;
  }
  
  const candidateId = candidate.id as string;
  const createdBy = candidate.createdBy as string || 'migration';
  const createdAt = candidate.createdAt as Date || new Date();
  
  for (const attr of attributes) {
    if (attr.value !== undefined && attr.value !== null && attr.value !== '') {
      proposals.push({
        id: `vp-${candidateId}-${attr.attributeKey}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        item_id: candidateId,
        attribute_key: attr.attributeKey,
        value: attr.value,
        status: 'accepted', // Existing values are already accepted
        proposed_by: createdBy,
        proposed_at: createdAt,
        accepted_by: createdBy,
        accepted_at: createdAt,
        source: 'migration',
      });
    }
  }
  
  return proposals;
}

/**
 * Migrate candidates from localStorage to Supabase
 * Includes transformations: exit_status, altitude, dedupeStatus
 * Also extracts ValueProposal records from existing attribute values
 */
async function migrateCandidates(dryRun: boolean): Promise<{ 
  count: number; 
  errors: string[];
  transformations: {
    detailedStateAdded: number;
    altitudeAdded: number;
    dedupeStatusAdded: number;
  };
  valueProposals: ValueProposal[];
}> {
  const errors: string[] = [];
  const transformations = {
    detailedStateAdded: 0,
    altitudeAdded: 0,
    dedupeStatusAdded: 0,
  };
  const allValueProposals: ValueProposal[] = [];
  
  const stored = readFromLocalStorage<{ candidates: unknown[] }>(STORAGE_KEYS.candidates);
  if (!stored?.candidates || stored.candidates.length === 0) {
    console.log('[Migration] No candidates to migrate');
    return { count: 0, errors, transformations, valueProposals: [] };
  }
  
  const candidates = stored.candidates.map(c => {
    let candidate = c as Record<string, unknown>;
    
    // Convert dates
    candidate = convertDates(candidate, ['createdAt', 'updatedAt', 'shapingStartedAt', 'shapedAt', 'deferredAt', 'deferReviewDate']);
    
    // Derive exit_status from existing state signals
    if (!(candidate as any).exit_status) {
      const migratedItem = migrateItem(candidate as any);
      (candidate as any).exit_status = (migratedItem as any).exit_status ?? 'active';
      (transformations as any).exitStatusDerived = ((transformations as any).exitStatusDerived ?? 0) + 1;
    }
    
    // Add altitude (default based on inference or 'feature')
    if (!candidate.altitude) {
      candidate.altitude = inferAltitude(candidate);
      transformations.altitudeAdded++;
    }
    
    // Add dedupeStatus (default to 'pending')
    if (!candidate.dedupeStatus) {
      candidate.dedupeStatus = 'pending';
      transformations.dedupeStatusAdded++;
    }
    
    // Extract value proposals from existing attribute values
    const proposals = extractValueProposals(candidate);
    allValueProposals.push(...proposals);
    
    return candidate;
  });
  
  console.log(`[Migration] Found ${candidates.length} candidates to migrate`);
  console.log(`[Migration] Transformations: ${JSON.stringify(transformations)}`);
  console.log(`[Migration] Extracted ${allValueProposals.length} value proposals`);
  
  if (dryRun) {
    return { count: candidates.length, errors, transformations, valueProposals: allValueProposals };
  }
  
  const result = await upsertMany(TABLE_NAMES.candidates, candidates as { id: string }[]);
  
  if (result.error) {
    errors.push(`Candidates: ${result.error}`);
    return { count: 0, errors, transformations, valueProposals: [] };
  }
  
  return { count: candidates.length, errors, transformations, valueProposals: allValueProposals };
}

/**
 * Migrate DEGAP cycles from localStorage to Supabase
 */
async function migrateCycles(dryRun: boolean): Promise<{ count: number; errors: string[] }> {
  const errors: string[] = [];
  
  const stored = readFromLocalStorage<{ cycles: unknown[] }>(STORAGE_KEYS.cycles);
  if (!stored?.cycles || stored.cycles.length === 0) {
    console.log('[Migration] No cycles to migrate');
    return { count: 0, errors };
  }
  
  const cycles = stored.cycles.map(c => {
    const cycle = c as Record<string, unknown>;
    return convertDates(cycle, ['createdAt', 'updatedAt', 'startedAt', 'completedAt']);
  });
  
  console.log(`[Migration] Found ${cycles.length} cycles to migrate`);
  
  if (dryRun) {
    return { count: cycles.length, errors };
  }
  
  const result = await upsertMany(TABLE_NAMES.cycles, cycles as { id: string }[]);
  
  if (result.error) {
    errors.push(`Cycles: ${result.error}`);
    return { count: 0, errors };
  }
  
  return { count: cycles.length, errors };
}

/**
 * Migrate sessions from localStorage to Supabase
 */
async function migrateSessions(dryRun: boolean): Promise<{ count: number; errors: string[] }> {
  const errors: string[] = [];
  const sessionIdMap = new Map<string, string>();
  
  const stored = readFromLocalStorage<{ sessions: unknown[] }>(STORAGE_KEYS.sessions);
  if (!stored?.sessions || stored.sessions.length === 0) {
    console.log('[Migration] No sessions to migrate');
    return { count: 0, errors };
  }
  
  const sessions = stored.sessions.map(s => {
    let session = s as Record<string, unknown>;
    session = convertDates(session, ['createdAt', 'updatedAt', 'criteriaFinalizedAt', 'runCompletedAt', 'publishedAt', 'scheduledFor']);
    
    // Ensure ID is a valid UUID
    const originalId = session.id as string;
    session.id = ensureUUID(originalId, sessionIdMap);
    
    // Also ensure cycle_id is a valid UUID if present
    if (session.cycleId && typeof session.cycleId === 'string') {
      session.cycleId = isValidUUID(session.cycleId) ? session.cycleId : null;
    }
    
    return session;
  });
  
  console.log(`[Migration] Found ${sessions.length} sessions to migrate`);
  if (sessionIdMap.size > 0) {
    console.log(`[Migration] Generated ${sessionIdMap.size} new UUIDs for sessions with non-UUID IDs`);
  }
  
  if (dryRun) {
    return { count: sessions.length, errors };
  }
  
  const result = await upsertMany(TABLE_NAMES.sessions, sessions as { id: string }[]);
  
  if (result.error) {
    errors.push(`Sessions: ${result.error}`);
    return { count: 0, errors };
  }
  
  return { count: sessions.length, errors };
}

/**
 * Migrate focus lists from localStorage to Supabase
 */
async function migrateFocusLists(dryRun: boolean): Promise<{ count: number; errors: string[] }> {
  const errors: string[] = [];
  
  const stored = readFromLocalStorage<{ focusLists: unknown[] }>(STORAGE_KEYS.focusLists);
  if (!stored?.focusLists || stored.focusLists.length === 0) {
    console.log('[Migration] No Item Pools to migrate');
    return { count: 0, errors };
  }
  
  const focusLists = stored.focusLists.map(l => {
    const list = l as Record<string, unknown>;
    return convertDates(list, ['createdAt', 'updatedAt', 'lastRankedAt', 'publishedAt']);
  });
  
  console.log(`[Migration] Found ${focusLists.length} Item Pools to migrate`);
  
  if (dryRun) {
    return { count: focusLists.length, errors };
  }
  
  const result = await upsertMany(TABLE_NAMES.focusLists, focusLists as { id: string }[]);
  
  if (result.error) {
    errors.push(`Item Pools: ${result.error}`);
    return { count: 0, errors };
  }
  
  return { count: focusLists.length, errors };
}

/**
 * Migrate clusters from localStorage to Supabase
 */
async function migrateClusters(dryRun: boolean): Promise<{ count: number; errors: string[] }> {
  const errors: string[] = [];
  
  const stored = readFromLocalStorage<{ clusters: unknown[]; memberships: unknown[] }>(STORAGE_KEYS.clusters);
  if (!stored?.clusters || stored.clusters.length === 0) {
    console.log('[Migration] No clusters to migrate');
    return { count: 0, errors };
  }
  
  const clusters = stored.clusters.map(c => {
    const cluster = c as Record<string, unknown>;
    return convertDates(cluster, ['createdAt', 'updatedAt']);
  });
  
  console.log(`[Migration] Found ${clusters.length} clusters to migrate`);
  
  if (dryRun) {
    return { count: clusters.length, errors };
  }
  
  // Migrate clusters
  const clustersResult = await upsertMany(TABLE_NAMES.clusters, clusters as { id: string }[]);
  if (clustersResult.error) {
    errors.push(`Clusters: ${clustersResult.error}`);
    return { count: 0, errors };
  }
  
  // Migrate memberships if present
  if (stored.memberships && stored.memberships.length > 0) {
    const memberships = stored.memberships.map(m => {
      const membership = m as Record<string, unknown>;
      return convertDates(membership, ['submittedAt', 'addedAt']);
    });
    
    const membershipsResult = await upsertMany(TABLE_NAMES.clusterMemberships, memberships as { id: string }[]);
    if (membershipsResult.error) {
      errors.push(`Cluster Memberships: ${membershipsResult.error}`);
    }
  }
  
  return { count: clusters.length, errors };
}

/**
 * Migrate attributes from localStorage to Supabase
 */
async function migrateAttributes(dryRun: boolean): Promise<{ count: number; errors: string[] }> {
  const errors: string[] = [];
  const attributeIdMap = new Map<string, string>();
  
  // First check if attributes already exist in Supabase (from seed.sql)
  const supabase = getSupabaseClient();
  const { data: existingAttrs } = await supabase.from(TABLE_NAMES.attributes).select('key').limit(1);
  
  if (existingAttrs && existingAttrs.length > 0) {
    console.log('[Migration] Attributes already exist in Supabase, skipping migration');
    return { count: 0, errors };
  }
  
  const stored = readFromLocalStorage<{ attributes: unknown[] }>(STORAGE_KEYS.attributes);
  if (!stored?.attributes || stored.attributes.length === 0) {
    console.log('[Migration] No attributes to migrate');
    return { count: 0, errors };
  }
  
  // Attributes need special handling - generate UUIDs and keep 'key' as a separate field
  const attributes = stored.attributes.map(a => {
    const attr = a as Record<string, unknown>;
    const key = attr.key as string;
    
    // Generate a proper UUID for the id field
    // The 'key' field remains for lookups (e.g., 'impact', 'effort')
    const id = ensureUUID(key, attributeIdMap);
    
    return { ...attr, id, key };
  });
  
  console.log(`[Migration] Found ${attributes.length} attributes to migrate`);
  if (attributeIdMap.size > 0) {
    console.log(`[Migration] Generated ${attributeIdMap.size} new UUIDs for attributes`);
  }
  
  if (dryRun) {
    return { count: attributes.length, errors };
  }
  
  const result = await upsertMany(TABLE_NAMES.attributes, attributes as { id: string }[]);
  
  if (result.error) {
    errors.push(`Attributes: ${result.error}`);
    return { count: 0, errors };
  }
  
  return { count: attributes.length, errors };
}

/**
 * Migrate org units from localStorage to Supabase
 */
async function migrateOrgUnits(dryRun: boolean): Promise<{ count: number; errors: string[] }> {
  const errors: string[] = [];
  
  const stored = readFromLocalStorage<{ orgUnitsArray?: unknown[] }>(STORAGE_KEYS.enterpriseIdentity);
  if (!stored?.orgUnitsArray || stored.orgUnitsArray.length === 0) {
    console.log('[Migration] No org units to migrate');
    return { count: 0, errors };
  }
  
  const orgUnits = stored.orgUnitsArray.map(o => {
    const orgUnit = o as Record<string, unknown>;
    return convertDates(orgUnit, ['createdAt', 'updatedAt']);
  });
  
  console.log(`[Migration] Found ${orgUnits.length} org units to migrate`);
  
  if (dryRun) {
    return { count: orgUnits.length, errors };
  }
  
  const result = await upsertMany(TABLE_NAMES.orgUnits, orgUnits as { id: string }[]);
  
  if (result.error) {
    errors.push(`Org Units: ${result.error}`);
    return { count: 0, errors };
  }
  
  return { count: orgUnits.length, errors };
}

/**
 * Clear localStorage data after successful migration
 */
function clearLocalStorageData(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    if (key !== STORAGE_KEYS.migrationComplete) {
      localStorage.removeItem(key);
    }
  });
  console.log('[Migration] Cleared localStorage data');
}

// ============================================================================
// Main Migration Function
// ============================================================================

/**
 * Run the full migration from localStorage to Supabase
 */
export async function migrateToSupabase(options: MigrationOptions = {}): Promise<MigrationResult> {
  const { clearLocalStorage = false, dryRun = false } = options;
  
  console.log('='.repeat(60));
  console.log('[Migration] Starting localStorage to Supabase migration');
  console.log(`[Migration] Mode: ${dryRun ? 'DRY RUN' : 'LIVE'}`);
  console.log('='.repeat(60));
  
  const result: MigrationResult = {
    success: false,
    migratedCounts: {
      candidates: 0,
      cycles: 0,
      sessions: 0,
      focusLists: 0,
      clusters: 0,
      attributes: 0,
      orgUnits: 0,
      valueProposals: 0,
    },
    transformations: {
      detailedStateAdded: 0,
      altitudeAdded: 0,
      dedupeStatusAdded: 0,
      valueProposalsExtracted: 0,
    },
    errors: [],
  };
  
  // Check if Supabase is available
  if (!isSupabaseAvailable()) {
    result.errors.push('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
    console.error('[Migration] Failed:', result.errors[0]);
    return result;
  }
  
  // Check if migration already complete
  if (isMigrationComplete() && !dryRun) {
    console.log('[Migration] Migration already completed. Skipping.');
    result.success = true;
    return result;
  }
  
  try {
    // Migrate candidates with transformations
    const candidatesResult = await migrateCandidates(dryRun);
    result.migratedCounts.candidates = candidatesResult.count;
    result.errors.push(...candidatesResult.errors);
    result.transformations.detailedStateAdded = candidatesResult.transformations.detailedStateAdded;
    result.transformations.altitudeAdded = candidatesResult.transformations.altitudeAdded;
    result.transformations.dedupeStatusAdded = candidatesResult.transformations.dedupeStatusAdded;
    result.transformations.valueProposalsExtracted = candidatesResult.valueProposals.length;
    
    // Migrate value proposals extracted from candidates
    if (candidatesResult.valueProposals.length > 0) {
      if (dryRun) {
        result.migratedCounts.valueProposals = candidatesResult.valueProposals.length;
      } else {
        const vpResult = await insertMany(TABLE_NAMES.valueProposals, candidatesResult.valueProposals as { id: string }[]);
        if (vpResult.error) {
          result.errors.push(`Value Proposals: ${vpResult.error}`);
        } else {
          result.migratedCounts.valueProposals = candidatesResult.valueProposals.length;
        }
      }
    }
    
    const cyclesResult = await migrateCycles(dryRun);
    result.migratedCounts.cycles = cyclesResult.count;
    result.errors.push(...cyclesResult.errors);
    
    const sessionsResult = await migrateSessions(dryRun);
    result.migratedCounts.sessions = sessionsResult.count;
    result.errors.push(...sessionsResult.errors);
    
    const focusListsResult = await migrateFocusLists(dryRun);
    result.migratedCounts.focusLists = focusListsResult.count;
    result.errors.push(...focusListsResult.errors);
    
    const clustersResult = await migrateClusters(dryRun);
    result.migratedCounts.clusters = clustersResult.count;
    result.errors.push(...clustersResult.errors);
    
    const attributesResult = await migrateAttributes(dryRun);
    result.migratedCounts.attributes = attributesResult.count;
    result.errors.push(...attributesResult.errors);
    
    const orgUnitsResult = await migrateOrgUnits(dryRun);
    result.migratedCounts.orgUnits = orgUnitsResult.count;
    result.errors.push(...orgUnitsResult.errors);
    
    // Mark migration complete if no errors and not dry run
    if (result.errors.length === 0 && !dryRun) {
      markMigrationComplete();
      
      // Optionally clear localStorage
      if (clearLocalStorage) {
        clearLocalStorageData();
      }
    }
    
    result.success = result.errors.length === 0;
    
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    result.errors.push(`Unexpected error: ${message}`);
    console.error('[Migration] Unexpected error:', error);
  }
  
  // Log summary
  console.log('='.repeat(60));
  console.log('[Migration] Migration Summary');
  console.log('='.repeat(60));
  console.log('Migrated counts:');
  Object.entries(result.migratedCounts).forEach(([key, count]) => {
    console.log(`  ${key}: ${count}`);
  });
  console.log('Data transformations:');
  Object.entries(result.transformations).forEach(([key, count]) => {
    console.log(`  ${key}: ${count}`);
  });
  if (result.errors.length > 0) {
    console.log('Errors:');
    result.errors.forEach(error => console.log(`  - ${error}`));
  }
  console.log(`Success: ${result.success}`);
  console.log('='.repeat(60));
  
  return result;
}

/**
 * Check what data would be migrated (dry run)
 */
export async function checkMigrationData(): Promise<MigrationResult> {
  return migrateToSupabase({ dryRun: true });
}

/**
 * Force re-run migration (use with caution)
 */
export async function forceReMigration(options: Omit<MigrationOptions, 'dryRun'> = {}): Promise<MigrationResult> {
  localStorage.removeItem(STORAGE_KEYS.migrationComplete);
  return migrateToSupabase(options);
}
